
import React, { useState, useMemo, useEffect } from 'react';
import { AIActionPlanStep, CHEMICALS, ChemicalProduct, ChemicalUnit } from '../types';
import { annotateUsageWithMetric } from '../utils/dosage';

interface AIActionPlanTabProps {
  plan: AIActionPlanStep[] | null;
  isLoading: boolean;
  error: string | null;
  poolVolume: number;
  onSavePlan?: (plan: AIActionPlanStep[]) => Promise<void>;
}

// --- Start of copied components and utils from RuleBasedActionPlanTab ---
const UNIT_CONVERSIONS: Partial<Record<ChemicalUnit, Partial<Record<ChemicalUnit, number>>>> = {
    lbs: { oz: 16, g: 453.592, kg: 0.453592 },
    oz: { lbs: 1 / 16, g: 28.3495, kg: 0.0283495 },
    g: { oz: 1 / 28.3495, lbs: 1 / 453.592, kg: 1 / 1000 },
    kg: { g: 1000, lbs: 1 / 0.453592, oz: 1 / 0.0283495 },
    gallons: { quarts: 4, fl_oz: 128, ml: 3785.41 },
    quarts: { gallons: 1 / 4, fl_oz: 32, ml: 946.353 },
    fl_oz: { quarts: 1 / 32, gallons: 1 / 128, ml: 29.5735 },
    ml: { fl_oz: 1 / 29.5735, quarts: 1 / 946.353, gallons: 1 / 3785.41 },
    tablets: {},
    bags: {}
};

const formatAmount = (amount: number, unit: ChemicalUnit): string => {
    if (unit === 'lbs' || unit === 'oz' || unit === 'kg') { return `${amount.toFixed(2)} ${unit}`; }
    if (unit === 'gallons' || unit === 'quarts' || unit === 'fl_oz') { return `${amount.toFixed(1)} ${unit}`; }
    if (unit === 'ml' || unit === 'g') { return `${Math.round(amount)} ${unit}`; }
    if (unit === 'tablets') { return `${amount.toFixed(1)} ${unit}`; }
    return `${Math.round(amount)} ${unit}`;
};

const getDosage = (product: ChemicalProduct, targetChange: number, changeType: 'ppm' | 'demand_drops', poolVolume: number): number | null => {
    const { formula } = product;
    const V10k = poolVolume / 10000;

    if (changeType === 'demand_drops' && formula.amountPerDrop) {
        return targetChange * V10k * formula.amountPerDrop;
    }
    if (changeType === 'ppm') {
        if (formula.amountPer10kGalToOnePpm) { return targetChange * V10k * formula.amountPer10kGalToOnePpm; }
        if (formula.baseAmount && formula.raisesBy && formula.perVolume) { return (targetChange / Math.abs(Number(formula.raisesBy))) * (poolVolume / formula.perVolume) * formula.baseAmount; }
    }
    return null;
};

const DosageCalculator: React.FC<{ products: ChemicalProduct[], dosageCalculation: (product: ChemicalProduct) => number | null }> = ({ products, dosageCalculation }) => {
    const [selectedProductId, setSelectedProductId] = useState(products[0]?.id || '');
    const selectedProduct = useMemo(() => products.find(p => p.id === selectedProductId), [products, selectedProductId]);
    const getDefaultUnit = (product?: ChemicalProduct): ChemicalUnit => product?.formula.baseUnit || product?.formula.unit || product?.formula.unitPerDrop || 'lbs';
    const [selectedUnit, setSelectedUnit] = useState<ChemicalUnit>(getDefaultUnit(selectedProduct));

    useEffect(() => { if (selectedProduct) { setSelectedUnit(getDefaultUnit(selectedProduct)); } }, [selectedProductId, selectedProduct]);

    const calculateDosage = () => {
        if (!selectedProduct) return "N/A";
        const baseAmount = dosageCalculation(selectedProduct);
        if (baseAmount === null || isNaN(baseAmount)) return "Calculation error";
        if (baseAmount <= 0) return "0";
        const baseUnit = getDefaultUnit(selectedProduct);
        if (baseUnit !== selectedUnit) {
            const factor = UNIT_CONVERSIONS[baseUnit]?.[selectedUnit];
            if (factor !== undefined) { return formatAmount(baseAmount * factor, selectedUnit); }
        }
        return formatAmount(baseAmount, selectedUnit);
    };

    const availableUnits: ChemicalUnit[] = useMemo(() => {
        const base = getDefaultUnit(selectedProduct);
        const weightUnits: ChemicalUnit[] = ['lbs', 'oz', 'g', 'kg'];
        const volumeUnits: ChemicalUnit[] = ['gallons', 'quarts', 'fl_oz', 'ml'];
        const discreteUnits: ChemicalUnit[] = ['tablets', 'bags'];

        if (weightUnits.includes(base)) return weightUnits;
        if (volumeUnits.includes(base)) return volumeUnits;
        if (discreteUnits.includes(base)) return [base];
        return [base];
    }, [selectedProduct]);

    if (!selectedProduct) return <p className="text-slate-400 text-sm">No specific products available for this AI recommendation.</p>;

    return (
        <div className="space-y-3 mt-2">
            <div>
                <label className="block text-sm font-medium text-slate-300">Choose Chemical:</label>
                <select value={selectedProductId} onChange={e => setSelectedProductId(e.target.value)} className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white">
                    {products.map(p => <option key={p.id} value={p.id}>{p.brand} - {p.productName}</option>)}
                </select>
            </div>
            <div className="flex items-end gap-3">
                <div className="flex-grow">
                    <label className="block text-sm font-medium text-slate-300">Dosage:</label>
                    <div className="mt-1 bg-slate-700 p-3 rounded-md text-cyan-300 text-lg font-bold min-h-[50px] flex items-center">{calculateDosage()}</div>
                </div>
                {availableUnits.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-slate-300">Unit:</label>
                        <select value={selectedUnit} onChange={e => setSelectedUnit(e.target.value as ChemicalUnit)} className="mt-1 w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-white">
                            {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>
                )}
            </div>
        </div>
    );
};
// --- End of copied components ---


const LoadingSpinner: React.FC = () => (
  <div className="flex flex-col items-center justify-center text-center p-8 bg-slate-800 rounded-lg">
    <svg className="animate-spin -ml-1 mr-3 h-10 w-10 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <p className="mt-4 text-lg text-white">Generating your personalized AI plan...</p>
    <p className="text-sm text-slate-400">This can take a few moments. We're analyzing your results to provide the best advice.</p>
  </div>
);

const getFunctionFromChemical = (chemical: string | undefined): string | null => {
    if (!chemical) return null;
    const lowerChem = chemical.toLowerCase();
    if (lowerChem.includes('bicarbonate')) return 'RAISE_TA';
    if (lowerChem.includes('carbonate') || lowerChem.includes('soda ash')) return 'RAISE_PH';
    if (lowerChem.includes('muriatic') || lowerChem.includes('bisulfate') || lowerChem.includes('dry acid')) return 'LOWER_PH_TA';
    if (lowerChem.includes('hypochlorite') || lowerChem.includes('dichloro') || lowerChem.includes('trichlor')) return 'ADD_FC';
    if (lowerChem.includes('calcium chloride')) return 'RAISE_CH';
    if (lowerChem.includes('cyanuric') || lowerChem.includes('stabilizer')) return 'RAISE_CYA';
    return null;
}

const parseValue = (valueStr: string): number => parseFloat(valueStr) || 0;
const parseRange = (rangeStr: string): { lower: number, upper: number } => {
    const parts = rangeStr.replace(/ppm/gi, '').split('-').map(s => parseFloat(s.trim()));
    return { lower: parts[0] || 0, upper: parts[1] || parts[0] || 0 };
}


const PlanStep: React.FC<{ step: AIActionPlanStep, index: number, poolVolume: number }> = ({ step, index, poolVolume }) => {
    const chemicalFunction = getFunctionFromChemical(step.chemical);
    const products = chemicalFunction ? CHEMICALS.filter(c => c.functions.includes(chemicalFunction)) : [];

    const currentValue = parseValue(step.currentValue);
    const idealRange = parseRange(step.idealRange);

    // Target logic: if current value is low, target the lower bound. If high, target upper bound.
    const targetValue = currentValue < idealRange.lower ? idealRange.lower : idealRange.upper;
    const targetChange = targetValue - currentValue;

    return (
    <div className="bg-slate-800 p-4 rounded-lg shadow-md border-l-4 border-cyan-500 animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 bg-slate-700 text-cyan-400 font-bold rounded-full h-10 w-10 flex items-center justify-center text-xl">
          {index + 1}
        </div>
        <div className="flex-grow">
          <h3 className="text-xl font-bold text-white">{step.parameter}</h3>
          <p className="text-sm text-slate-400">
            Current: <span className="font-semibold text-white">{step.currentValue}</span> | Ideal: <span className="font-semibold text-green-400">{step.idealRange}</span>
          </p>
          <p className="mt-2 text-lg text-cyan-300">{step.action}</p>

          {step.dosages && step.dosages.length > 0 && (
            <div className="mt-3 bg-slate-900/50 p-3 rounded-md">
              <h4 className="font-semibold text-white mb-1">AI Dosage Recommendation:</h4>
              <p className="text-sm text-slate-400 mb-2">(Using generic chemical: <span className='italic'>{step.chemical || 'Unknown'}</span>)</p>
              <ul className="list-disc list-inside space-y-1 text-slate-300">
                {step.dosages.map((dosage, i) => <li key={i}>{annotateUsageWithMetric(dosage)}</li>)}
              </ul>
            </div>
          )}

           {products.length > 0 && Math.abs(targetChange) > 0.01 && (
              <div className="mt-3 border-t border-slate-700 pt-3">
                  <h4 className="font-semibold text-white mb-1">Or, Calculate for a Specific Product:</h4>
                  <p className="text-xs text-slate-400 mb-2">Based on AI's goal to adjust by {targetChange.toFixed(2)} ppm.</p>
                  <DosageCalculator products={products} dosageCalculation={p => getDosage(p, Math.abs(targetChange), 'ppm', poolVolume)}/>
              </div>
            )}


          <p className="mt-3 text-sm text-slate-400 italic">
            <strong className="font-semibold text-slate-300">AI Note:</strong> {step.notes}
          </p>
        </div>
      </div>
    </div>
    );
};

export const AIActionPlanTab: React.FC<AIActionPlanTabProps> = ({ plan, isLoading, error, poolVolume, onSavePlan }) => {
  const [savingPlan, setSavingPlan] = useState(false);
  const [savedPlan, setSavedPlan] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSavePlan = async () => {
    if (!plan || !onSavePlan) return;
    setSavingPlan(true);
    setSavedPlan(false);
    setSaveError(null);
    try {
      await onSavePlan(plan);
      setSavedPlan(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingPlan(false);
    }
  };
  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="bg-red-900/50 border border-red-700 text-red-300 p-4 rounded-lg text-center">
        <h2 className="font-bold text-lg">Error Generating AI Plan</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!plan || plan.length === 0) {
    return (
      <div className="text-center text-slate-400 py-8 bg-slate-800 rounded-lg">
        <p className="text-lg">No AI action plan to display.</p>
        <p>Complete a test on the "New Test" tab to generate a new AI plan.</p>
      </div>
    );
  }

  return (
    <div className="printable-content space-y-4 animate-fade-in">
        <div className="bg-slate-800 p-4 rounded-lg shadow-md">
            <div className="flex justify-between items-center flex-wrap gap-2">
                <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold text-white">Your AI-Powered Action Plan</h2>
                    <p className="text-slate-400 text-sm">Based on a {poolVolume > 0 ? poolVolume.toLocaleString() : ''} gallon pool.</p>
                </div>
                <div className="no-print flex items-center gap-2 flex-shrink-0">
                    {onSavePlan && (
                      <button
                        onClick={handleSavePlan}
                        disabled={savingPlan}
                        className="bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white font-bold py-2 px-3 rounded-lg transition-colors duration-300 flex items-center gap-2 text-sm"
                        title="Save this AI plan to the Pi"
                      >
                        📊 {savingPlan ? 'Saving…' : savedPlan ? '✅ Saved!' : 'Save to Pi'}
                      </button>
                    )}
                    <button onClick={() => window.print()} className="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-3 rounded-lg transition-colors duration-300 flex items-center gap-2 text-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                        Print
                    </button>
                </div>
            </div>
            {saveError && (
              <p className="mt-2 text-sm text-red-400">⚠️ {saveError}</p>
            )}
        </div>
        {plan.map((step, index) => (
            <PlanStep key={index} step={step} index={index} poolVolume={poolVolume} />
        ))}
    </div>
  );
};
